'use client';

import React, { useState } from 'react';
import { FileText, Link, Mic, ChevronRight, Layers, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import type { WizardData } from './ContentGeneratorWizard';

interface Props {
  data: WizardData;
  onUpdate: (partial: Partial<WizardData>) => void;
  onNext: () => void;
}

const inputTypes = [
  { id: 'input-text', value: 'text' as const, icon: <FileText size={18} />, label: 'Text-Eingabe', desc: 'Füge einen Artikel, Bericht oder beliebigen Text ein.' },
  { id: 'input-url', value: 'url' as const, icon: <Link size={18} />, label: 'URL-Scraper', desc: 'Gib eine Webseite an — wir extrahieren den Inhalt automatisch.' },
  { id: 'input-audio', value: 'audio' as const, icon: <Mic size={18} />, label: 'Audio-Upload', desc: 'Lade eine Sprachaufnahme hoch — wir transkribieren und verarbeiten sie.' },
];

const DURATION_OPTIONS = [
  { value: 1, label: '1 Tag', desc: '1 Post' },
  { value: 3, label: '3 Tage', desc: '3 Posts' },
  { value: 7, label: '7 Tage', desc: '7 Posts' },
  { value: 14, label: '14 Tage', desc: '14 Posts' },
  { value: 21, label: '21 Tage', desc: '21 Posts' },
];

type BatchStatus = 'idle' | 'generating' | 'success' | 'error';

export default function WizardStep1Input({ data, onUpdate, onNext }: Props) {
  const [urlValue, setUrlValue] = useState('');

  // Batch automation state
  const [batchDuration, setBatchDuration] = useState<1 | 3 | 7 | 14 | 21>(14);
  const [batchStatus, setBatchStatus] = useState<BatchStatus>('idle');
  const [batchCount, setBatchCount] = useState(0);
  const [batchError, setBatchError] = useState<string | null>(null);

  const canProceed =
    (data.inputType === 'text' && data.inputContent.trim().length > 50) ||
    (data.inputType === 'url' && urlValue.trim().startsWith('http')) ||
    (data.inputType === 'audio');

  const handleBatchGenerate = async () => {
    if (batchStatus === 'generating') return;
    setBatchStatus('generating');
    setBatchError(null);
    setBatchCount(0);

    try {
      const res = await fetch('/api/content/batch-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          duration: batchDuration,
          topic: data.inputContent.trim() || undefined,
          url: urlValue.trim().startsWith('http') ? urlValue.trim() : undefined,
        }),
      });

      const json = await res.json();

      if (!res.ok || json.error) {
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }

      setBatchCount(json.count ?? batchDuration);
      setBatchStatus('success');
      toast.success(`${json.count} Distortion-Posts erstellt!`, {
        description: `${batchDuration} Tage Content-Kalender wurde in der Datenbank gespeichert.`,
      });
    } catch (err: any) {
      setBatchStatus('error');
      setBatchError(err.message ?? 'Unbekannter Fehler');
      toast.error('Batch-Generierung fehlgeschlagen: ' + (err.message ?? 'Unbekannter Fehler'));
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      {/* Left: Input type selector */}
      <div className="space-y-3">
        <p className="section-label mb-3" id="input-type-label">Input-Typ wählen</p>
        <div role="radiogroup" aria-labelledby="input-type-label" className="space-y-3">
          {inputTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => onUpdate({ inputType: type.value })}
              className={`w-full text-left p-4 rounded border transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-accent ${
                data.inputType === type.value
                  ? 'border-accent bg-accent/8 neon-glow' :'border-border bg-surface hover:border-accent/40'
              }`}
              role="radio"
              aria-checked={data.inputType === type.value}
              aria-label={`${type.label}: ${type.desc}`}
            >
              <div className="flex items-center gap-3 mb-1.5">
                <span className={data.inputType === type.value ? 'text-accent' : 'text-muted'} aria-hidden="true">
                  {type.icon}
                </span>
                <span className={`font-600 text-sm ${data.inputType === type.value ? 'text-accent' : 'text-white'}`}>
                  {type.label}
                </span>
              </div>
              <p className="text-xs text-muted leading-relaxed">{type.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Right: Input area */}
      <div className="xl:col-span-2 space-y-4">
        <div className="punk-card p-5">
          <p className="section-label mb-3">
            {data.inputType === 'text' ? 'Dein Ausgangstext' : data.inputType === 'url' ? 'Webseiten-URL' : 'Audio-Datei hochladen'}
          </p>

          {data.inputType === 'text' && (
            <div>
              <label className="label-punk" htmlFor="input-content">Inhalt einfügen</label>
              <p className="text-xs text-muted-2 mb-2" id="input-content-desc">Mindestens 50 Zeichen. Empfohlen: 500–3.000 Zeichen für optimale KI-Ergebnisse.</p>
              <textarea
                id="input-content"
                className="textarea-punk"
                style={{ minHeight: '280px' }}
                placeholder="Füge hier deinen Ausgangstext ein — einen Blogbeitrag, eine Pressemitteilung, Meetingnotizen oder eine Idee. Die KI verwandelt ihn in 14 Tage Content für alle Plattformen..."
                value={data.inputContent}
                onChange={(e) => onUpdate({ inputContent: e.target.value })}
                aria-describedby="input-content-desc input-content-status"
                aria-required="true"
              />
              <div className="flex justify-between mt-2">
                <span className="mono text-xs text-muted" aria-live="polite" id="input-content-status">
                  {data.inputContent.length} Zeichen
                  {data.inputContent.length < 50 && data.inputContent.length > 0 && ' — Mindestens 50 Zeichen erforderlich'}
                  {data.inputContent.length >= 50 && ' — Bereit'}
                </span>
                {data.inputContent.length < 50 && data.inputContent.length > 0 && (
                  <span className="mono text-xs text-yellow-400" aria-hidden="true">Mindestens 50 Zeichen erforderlich</span>
                )}
                {data.inputContent.length >= 50 && (
                  <span className="mono text-xs text-green-400" aria-hidden="true">✓ Bereit</span>
                )}
              </div>
            </div>
          )}

          {data.inputType === 'url' && (
            <div>
              <label className="label-punk" htmlFor="url-input">Webseiten-URL</label>
              <p className="text-xs text-muted-2 mb-2" id="url-input-desc">Gib eine vollständige URL an (z.B. Blogbeitrag, Unternehmensseite, Pressemitteilung).</p>
              <input
                id="url-input"
                type="url"
                className="input-punk"
                placeholder="https://www.berger-gmbh.de/blog/artikel-ueber-innovation"
                value={urlValue}
                onChange={(e) => setUrlValue(e.target.value)}
                aria-describedby="url-input-desc url-input-error"
                aria-invalid={!!(urlValue && !urlValue.startsWith('http'))}
                autoComplete="url"
              />
              {urlValue && !urlValue.startsWith('http') && (
                <p
                  id="url-input-error"
                  className="text-xs text-red-400 mt-1 mono"
                  role="alert"
                  aria-live="polite"
                >
                  Ungültige URL — muss mit https:// beginnen
                </p>
              )}
              <div className="mt-4 p-4 bg-surface border border-border rounded">
                <p className="text-xs text-muted">
                  Hinweis: Der URL-Scraper extrahiert automatisch Titel, Hauptinhalt und Metadaten der Seite.
                </p>
              </div>
            </div>
          )}

          {data.inputType === 'audio' && (
            <div>
              <label className="label-punk" htmlFor="audio-upload">Audio-Datei</label>
              <p className="text-xs text-muted-2 mb-3" id="audio-upload-desc">MP3, WAV, M4A · max. 50 MB · bis 60 Minuten</p>
              <div
                className="border-2 border-dashed border-border rounded p-10 text-center hover:border-accent/50 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent"
                role="button"
                tabIndex={0}
                aria-label="Audio-Datei hochladen — hierher ziehen oder klicken"
                aria-describedby="audio-upload-desc"
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.preventDefault(); }}
              >
                <Mic size={32} className="text-muted mx-auto mb-3" aria-hidden="true" />
                <p className="text-sm text-white font-500">Audio-Datei hierher ziehen</p>
                <p className="text-xs text-muted mt-1">oder klicken um eine Datei auszuwählen</p>
              </div>
            </div>
          )}

          <div className="mt-5 flex justify-between items-center pt-4 border-t border-border">
            <div>
              <label className="label-punk" htmlFor="project-name">Projektname</label>
              <input
                id="project-name"
                type="text"
                className="input-punk w-64"
                placeholder="z.B. Q2-Kampagne April 2026"
                value={data.projectName}
                onChange={(e) => onUpdate({ projectName: e.target.value })}
                aria-label="Projektname für diese Content-Generierung"
              />
            </div>
            <button
              onClick={onNext}
              disabled={!canProceed}
              className="btn-primary focus:outline-none focus:ring-2 focus:ring-accent"
              aria-label="Zum nächsten Schritt: Konfiguration"
              aria-disabled={!canProceed}
            >
              Weiter
              <ChevronRight size={14} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* ── Content Batching Automation (URL-Scraping section) ── */}
        {data.inputType === 'url' && (
          <div className="punk-card p-5 border border-accent/20">
            <div className="flex items-center gap-2 mb-4">
              <Layers size={16} className="text-accent" aria-hidden="true" />
              <p className="section-label text-accent">Content-Batching Automation</p>
            </div>
            <p className="text-xs text-muted mb-4 leading-relaxed">
              Generiere automatisch einen mehrtägigen Content-Kalender im <span className="text-white font-500">Aging Punk</span>-Stil — jeder Tag erhält eine einzigartige Distortion-Headline, eine satirische Caption und einen Platzhalter für das Gemini-Visual.
            </p>

            {/* Duration selector */}
            <div className="mb-4">
              <p className="label-punk mb-2">Zeitraum wählen</p>
              <div className="flex gap-2" role="radiogroup" aria-label="Batch-Zeitraum">
                {DURATION_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setBatchDuration(opt.value as 1 | 3 | 7 | 14 | 21)}
                    className={`flex-1 py-2.5 px-3 rounded border text-sm font-600 transition-all focus:outline-none focus:ring-2 focus:ring-accent ${
                      batchDuration === opt.value
                        ? 'border-accent bg-accent/10 text-accent neon-glow' :'border-border bg-surface text-muted hover:border-accent/40 hover:text-white'
                    }`}
                    role="radio"
                    aria-checked={batchDuration === opt.value}
                    aria-label={`${opt.label}: ${opt.desc}`}
                  >
                    <span className="block">{opt.label}</span>
                    <span className="block text-xs font-400 opacity-70">{opt.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Status feedback */}
            {batchStatus === 'success' && (
              <div className="flex items-center gap-2 mb-3 p-3 rounded bg-green-900/20 border border-green-700/40">
                <CheckCircle2 size={14} className="text-green-400 shrink-0" />
                <p className="text-xs text-green-400">
                  <span className="font-600">{batchCount} Posts</span> erfolgreich erstellt und im Content-Editor gespeichert.
                </p>
              </div>
            )}
            {batchStatus === 'error' && batchError && (
              <div className="flex items-start gap-2 mb-3 p-3 rounded bg-red-900/20 border border-red-700/40">
                <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
                <p className="text-xs text-red-400">{batchError}</p>
              </div>
            )}

            {/* Generate button */}
            <button
              onClick={handleBatchGenerate}
              disabled={batchStatus === 'generating'}
              className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded font-600 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-accent ${
                batchStatus === 'generating' ?'bg-accent/20 text-accent/60 cursor-not-allowed border border-accent/20' :'bg-accent text-black hover:bg-accent/90 active:scale-[0.98]'
              }`}
              aria-label={`${batchDuration} Distortion-Posts generieren`}
              aria-busy={batchStatus === 'generating'}
            >
              {batchStatus === 'generating' ? (
                <>
                  <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                  <span>Generiere {batchDuration} Posts…</span>
                </>
              ) : (
                <>
                  <Layers size={14} aria-hidden="true" />
                  <span>
                    {batchStatus === 'success' ? `Erneut generieren (${batchDuration} Tage)` : `${batchDuration} Tage generieren`}
                  </span>
                </>
              )}
            </button>

            <p className="text-xs text-muted-2 mt-2 text-center">
              Jeder Post wird mit einem Gemini-Visual-Platzhalter und konsekutivem Datum gespeichert.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}